import { ActionType } from '../../../core/redux/actionType';
import { ApiReducer, makeApiAction, makeApiReducer, makeRsaaTypes } from '../../../core/api/rsaa';
import { addError, addLoading, noLoadingNoError, getLoading } from '../../../core/api/module';
import * as R from 'ramda';
import { Endpoints } from '../../../core/api/endpoints';
import { Rater } from './models';
import { AppState } from '../../../core/redux/appState';
import { toMap } from '../../../language/arrays';


const KEY_ACTION = ActionType.AddRaterByEmailRequest;
const ACTIONS    = [
  ActionType.AddRaterByEmailRequest,
  ActionType.AddRaterByEmailResponse,
  ActionType.AddRaterByEmailError,
];


type Payload = Rater;


type Meta = {
  email: string;
  projectId: number;
};


/**
 * Dispatch this to associate a new rater with the specified project
 *
 * @param projectId - the database Id for the project to which this person should be added
 * @param participantId - the database Id for the participant to be rated
 * @param levelId - the rater's permissions level
 * @param email - the rater's email address
 * @param firstName - the rater's first name
 * @param lastName - the rater's last name
 */
export const addRaterByEmailAction = (
  projectId: number,
  participantId: number,
  levelId: number,
  email: string,
  firstName: string,
  lastName: string,
) => makeApiAction<Payload, Meta>(
  {
    method:   'POST',
    endpoint: Endpoints.AddRaterByEmail,
    headers:  { 'content-type': 'application/json' },
    body:     JSON.stringify(
      {
        projectId:     projectId,
        participantId: participantId,
        levelId:       levelId,
        email:         email,
        firstName:     firstName,
        lastName:      lastName,
      },
    ),
    types:    makeRsaaTypes<Payload, Meta>(
      ACTIONS,
      {
        email:     email,
        projectId: projectId,
      },
    ),
    bailout:  (state: AppState) => !!getLoading(state, KEY_ACTION, `${email}-${projectId}`),
  },
);

const reducer: ApiReducer<Payload, Meta> = (state, action, errorMsg, isLoading, payload) => {
  const email     = action.meta.email;
  const projectId = action.meta.projectId;
  const metaTag   = `${email}-${projectId}`;

  if (isLoading) {
    return addLoading(state, KEY_ACTION, metaTag);
  }

  if (errorMsg) {
    return addError(state, KEY_ACTION, errorMsg, metaTag);
  }

  const successState = noLoadingNoError(state, KEY_ACTION, metaTag);

  const requirements = toMap(successState.participantRequirements || [], req => `${req.surveyLevelId}`);

  const requirementToUpdate = requirements[payload?.levelId ?? -1];

  const withRaterAdded = R.assocPath(
    ['projectRaters', `${payload!.projectId}`, `${payload!.raterId}`],
    payload,
    successState,
  );

  if (!requirementToUpdate) {
    return withRaterAdded;
  }

  const ratersWithUpdatedCount = R.assocPath(
    [`${payload?.levelId}`, 'raterCount'],
    requirementToUpdate.raterCount + 1,
    requirements,
  );

  return R.assoc('participantRequirements', Object.values(ratersWithUpdatedCount), withRaterAdded);
};

export const addRaterByEmailReducer = makeApiReducer<Payload, Meta>(ACTIONS, KEY_ACTION, reducer);
